import { IResponse } from '@estee/elc-http-transport';
import { IPGKitItems } from '~api/KitsAPI/utils/IKits';
import {
    ICartMessage,
    ICartOffers,
    ICartOffer,
    ICartBuyNowPayLater
} from '~domain/entities/Cart/ICart';
import { PropsObject, typeConfigs } from '~api/utils/sanitizeData';

export interface IOrderItems {
    APPLIED_PRICE: number;
    APPLIED_TAX: number;
    ITEM_QUANTITY: number;
    UNIT_PRICE: number;
    COLLECTION_ID: number;
    'prod.PRODUCT_ID': string;
    'prod.PROD_BASE_NAME': string;
    'prod.MEDIUM_IMAGE': string | string[];
    'prod.SMALL_IMAGE': string | string[];
    'prod.PROD_RGN_NAME': string;
    'prod.PROD_RGN_SUBHEADING': string;
    'prod.url': string;
    'prod.isHazmat': boolean;
    'sku.SKU_BASE_ID': string;
    'sku.HEX_VALUE_STRING': string;
    'sku.HEX_VALUE': string;
    'sku.PRODUCT_SIZE': string;
    'sku.PRICE': number;
    'sku.PRICE2': number;
    'sku.label': string;
    'sku.REFILLABLE': number;
    'sku.isOutOfStock': number;
    'sku.SHADENAME': string;
    'sku.IS_ENGRAVABLE': boolean;
    DISCOUNT_AMOUNT: number;
    discountable: boolean;
    isSample: boolean;
    isSampleItem: boolean | number;
    isKitItem: boolean | number;
    isGiftItem: boolean;
    isDonationItem: boolean;
    isFreeVirtualSkuItem: boolean;
    isLoyaltyReward: boolean;
    loyaltyRewardCostInPoints: number;
    isWpOffer: boolean;
    OFFER_CODE: string;
    itemType?: string;
    replenishmentItemForm: {
        csrf_token: string;
        REPLENISHMENT_FREQ: {
            optionsGroup: { label: string; value: string; is_chosen: boolean }[];
        };
    };
    CART_ID: number;
    IS_ENGRAVABLE: boolean;
    'offer.benefit.Bogo'?: boolean;
    'offer.benefit.FixedPrice'?: boolean;
    'offer.benefit.FlatDiscount'?: boolean;
    'offer.benefit.PercentDiscount'?: boolean;
    'offer.benefit.ProDiscount'?: boolean;
    'offer.benefit.FullSizeSkus'?: boolean;
    'offer.benefit.Skus'?: boolean;
    'offer.benefit.FullSizeChoiceOfSkus'?: boolean;
    'offer.benefit.OfferCartPercentDiscount'?: boolean;
    DISCOUNT_PRICE?: number;
    discountPercent?: number;
    discountedAppliedPrice?: number;
    DISCOUNT_PCT?: number;
    maxAllowedQty?: number;
    'prod.LARGE_IMAGE': string | string[];
    'prod.IMAGE_L': string | string[];
    'prod.IMAGE_M': string | string[];
    'prod.IMAGE_S': string | string[];
    'prod.THUMBNAIL_IMAGE': string | string[];
    'sku.IMAGE_MEDIUM': string | string[];
    'sku.LARGE_ALT_IMAGES': string | string[];
    'sku.LARGE_IMAGE': string | string[];
    'sku.MEDIUM_IMAGE': string | string[];
    'sku.SMALL_IMAGE': string | string[];
    'sku.IMAGE_L': string | string[];
    'sku.IMAGE_ALT_L': string | string[];
    'sku.IMAGE_M': string | string[];
    'sku.IMAGE_ALT_M': string | string[];
    'sku.IMAGE_S': string | string[];
    'sku.IMAGE_SMALL': string | string[];
    'sku.IMAGE_ALT_S': string | string[];
    'sku.THUMBNAIL_IMAGE': string | string[];
}

export interface IOrderTotals {
    PRODUCT_SUM_W_TAX: number;
    SHIPPING_W_TAX: number;
    TOTAL_COST_W_TAX: number;
    TOTAL_TAX: number;
    SUB_TOTAL: number;
    max_total_item_limit?: number;
    display_sales_tax_cart: boolean;
}
export interface ITransOfferObject {
    benefit_class: string;
    benefit_value?: string | number;
    offer_name: string;
    discount?: string;
}

export interface IOrderObject {
    order: {
        items: IOrderItems[];
        samples: IOrderItems[];
        kits: IPGKitItems[];
        giftWrap: number;
        SHIP_METHOD: number;
        address?: {
            ADDRESS_ID?: number;
        };
    };
    totals: IOrderTotals | null;
    subtotal: number;
    subtotal_no_tax: number;
    default_cart_id: number;
    formattedSubtotal: string;
    formattedSubtotalNoTax: string;
    hasOrderableItems: boolean;
    hasPreOrderItemOnly: boolean;
    hasPreOrderItems: boolean;
    items_count: number;
    formattedTotal: string;
    formattedTaxRate: string;
    formattedTotalTax: string;
    max_total_item_limit: number;
    display_sales_tax_cart: boolean;
    trans: {
        TRANS_ID: number;
        isDiscounted: boolean;
        TOTAL_COST: number;
        TOTAL_APPLIED_TAX: number;
        TOTAL_SHIPPING_APPLIED_PRICE: number;
        TOTAL_ITEMS_BASE_PRICE: number;
        TOTAL_ITEMS_DISCOUNT: number;
        TOTAL_GIFTWRAP_APPLIED_PRICE: number;
        getDonation: number;
        getDonationsDerivedSubtotal: number;
        total_applied_vat: number;
        totalItemsDiscount: number;
        formattedTotalItemsDiscount: string;
        ['formatted.TOTAL_COST']: string;
        ['formatted.TOTAL_ITEMS_BASE_PRICE']: string;
        ['formatted.TOTAL_ITEMS_DISCOUNT']: string;
        ['formatted.TOTAL_APPLIED_TAX']: string;
        ['formatted.TOTAL_SHIPPING_APPLIED_PRICE']: string;
        ['formatted.TOTAL_GIFTWRAP_APPLIED_PRICE']: string;
        totals: {
            TOTAL_COST_W_TAX: number;
            TOTAL_TAX: number;
        };
        paymentInstallments?: ICartBuyNowPayLater;
        loyaltyPoints?: string;
        TOTAL_RETAIL_DELIVERY_FEE: number | null;
    };
    messages?: ICartMessage[];
    offers?: ICartOffers;
    wp_samples?: ICartOffer[];
    pwp?: ICartOffer[];
    user?: IOrderUser;
    TOTAL_APPLIED_TAX: number;
    offers_summary: ITransOfferObject[];
    appliedOffersData?: {
        [key: string]: {
            activation: string;
        };
    };
}
export interface IToken {
    token: string;
}

export interface ICollectionData {
    COLLECTION_ID: number;
    COLLECTION_NAME: string;
    COLLECTION_TYPE: string;
    REPLENISHMENT_FREQ: number;
}

export interface ICollection {
    collection: ICollectionData;
}

export interface IACResults {
    result: {
        CARTITEM: IOrderItems;
        PREVIOUS_ITEM_QUANTITY: number;
    };
}

export interface IPGResponse<T> {
    id?: number;
    jsonrpc?: string;
    error?: {
        data: {
            messages: { text: string }[];
            trans_data: T;
        };
    };
    result: {
        data: {
            ac_results?: IACResults[];
            collection_id: string;
            coll_info: ICollectionData;
            trans_data: T | null;
        };
        value: T;
    };
}

export interface IUpdatePayload {
    skus: string[];
    quantity: number;
    token?: string | Promise<string>;
    cartId?: number;
    collectionId?: number;
}

export interface IRemovePayload {
    skus: string[];
    cartId?: number;
    collectionId?: number;
}

export interface IRPCData<T> {
    response: IResponse<IPGResponse<T>[]>;
    requestType?: string;
}

export interface ICartItemsPayload {
    trans_fields: string[];
    order_fields: string[];
    sku_fields: string[];
    payment_fields: string[];
}

export interface ICheckoutMessagesPayload {
    _SUBMIT: string;
}

export interface IOrderUser {
    registered_user: number;
    email_address: string;
    session_id: string;
    signed_in: number;
    first_name: string;
    recognized_user: number;
    last_name: string;
    user_id: string;
}

export const OrderUserValidationConfig: PropsObject = {
    registered_user: typeConfigs.boolean(),
    email_address: typeConfigs.string(),
    session_id: typeConfigs.number(),
    signed_in: typeConfigs.boolean(),
    first_name: typeConfigs.string(),
    recognized_user: typeConfigs.boolean(),
    last_name: typeConfigs.string(),
    user_id: typeConfigs.number()
};

export interface IExtraMessagesRule {
    rule: { [key: string]: boolean };
    text: string;
}

export interface IExtraMessages {
    [key: string]: ICartMessage;
}

export interface IOverridenTranslationMessage {
    [key: string]: string;
}

export interface IFetchCartParams {
    deleteMessages?: boolean
}
